import { useState, useEffect, useCallback } from 'react';
import { Upload, MessageCircle, Globe, Lock } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import {
  useCreateRoom,
  useAssignRoomAdmin,
} from '../../hooks/queries/useSpaceQueries';
import { getImageUrl } from '../../utils/imageUrl';
import { ISpace } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

interface ICreateRoomDialogProps {
  isOpen: boolean;
  onClose: () => void;
  space: ISpace;
}

const INITIAL_FORM = {
  name: '',
  description: '',
  isPrivate: false,
  avatar: '',
};

const CreateRoomDialog = ({
  isOpen,
  onClose,
  space,
}: ICreateRoomDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createRoom = useCreateRoom();
  const assignAdmin = useAssignRoomAdmin();

  useEffect(() => {
    if (!isOpen) return;

    setForm({ ...INITIAL_FORM });
    setSelectedAdminId('');
    setError(null);
  }, [isOpen]);

  const handleSubmit = async () => {
    setError(null);

    try {
      const result = await createRoom.mutateAsync({
        spaceId: space.id,
        data: {
          ...form,
          isAnnouncement: false,
          memberIds: [],
        },
      });

      // Assign admin after room creation if one was selected
      if (selectedAdminId && result && (result as any).id) {
        try {
          await assignAdmin.mutateAsync({
            roomId: (result as any).id,
            userIds: [selectedAdminId],
          });
        } catch {
          // Room created successfully, admin assignment failed silently
        }
      }

      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const fetchSpaceMembers = useCallback(
    async (search?: string) => {
      try {
        const members = await api.get<any[]>(
          `/backoffice/spaces/${space.id}/members`,
        );

        const filtered = search
          ? members.filter(
              (m: any) =>
                m.user?.displayName
                  ?.toLowerCase()
                  .includes(search.toLowerCase()) ||
                m.user?.username?.toLowerCase().includes(search.toLowerCase()),
            )
          : members;

        return filtered.map((m: any) => ({
          id: m.userId,
          username: m.user?.username || m.userId,
          displayName: m.user?.displayName || m.user?.username || m.userId,
        }));
      } catch {
        return [];
      }
    },
    [space.id],
  );

  const handleAvatarUpload = async (file: File) => {
    try {
      const result = await api.upload<{ imageUrl: string }>(
        '/backoffice/upload-image',
        file,
      );
      setForm((prev) => ({ ...prev, avatar: result.imageUrl }));
    } catch (err: any) {
      alert(err.message || t('common.save_failed'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {t('rooms.create_title', { name: space.name })}
          </DialogTitle>
          <DialogDescription>
            {t('rooms.create_room_desc', { name: space.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Avatar */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('chatroom.room_avatar')}
            </label>
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                style={{
                  background: form.avatar
                    ? 'transparent'
                    : 'linear-gradient(135deg, #A855F7, #EC4899)',
                }}
              >
                {form.avatar ? (
                  <img
                    src={getImageUrl(form.avatar)!}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <MessageCircle className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" asChild>
                    <span className="flex items-center gap-1.5">
                      <Upload className="w-3.5 h-3.5" />
                      {t('rooms.upload_avatar')}
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      if (file) handleAvatarUpload(file);
                      e.target.value = '';
                    }}
                  />
                </label>
                {form.avatar && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm((prev) => ({ ...prev, avatar: '' }))}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Room name */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('rooms.room_name')} *
            </label>
            <Input
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder={t('rooms.room_name_placeholder')}
              autoFocus
            />
          </div>

          {/* Assign Admin */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('rooms.assign_admin')}
            </label>
            <UserAutocomplete
              value={selectedAdminId}
              onChange={setSelectedAdminId}
              fetchUsers={fetchSpaceMembers}
              placeholder={t('spaces.search_users')}
              triggerPlaceholder={t('rooms.select_admin_placeholder')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('rooms.admin_must_be_member')}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('common.description')}
            </label>
            <Textarea
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder={t('rooms.desc_placeholder')}
              rows={3}
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('spaces.visibility')}
            </label>
            <Select
              value={form.isPrivate ? 'private' : 'public'}
              onValueChange={(val) =>
                setForm((prev) => ({ ...prev, isPrivate: val === 'private' }))
              }
            >
              <SelectTrigger className="w-full h-auto py-2.5">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    {form.isPrivate ? (
                      <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    ) : (
                      <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">
                        {form.isPrivate
                          ? t('chatroom.private_room')
                          : t('chatroom.public_room')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {form.isPrivate
                          ? t('chatroom.private_room_desc')
                          : t('chatroom.public_room_desc')}
                      </div>
                    </div>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public" className="py-2.5">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('chatroom.public_room')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('chatroom.public_room_desc')}
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="private" className="py-2.5">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('chatroom.private_room')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('chatroom.private_room_desc')}
                      </div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.name.trim() || createRoom.isPending}
          >
            {createRoom.isPending ? t('common.saving') : t('rooms.create_room')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoomDialog;
